import {
  ShareScreenBase,
  SKIP_WAITLIST_TITLE,
  skipWaitlistSuccessMessage,
} from "./share-screen-base"
import { getRoomDeeplinkUrl } from "~/common/platform"
import { getMetaContent } from "~/common/get-meta-content"
import { RoomContentCard } from "./room-content-card"
import {
  getEpisodeData,
  getInviterData,
  getMessageData,
  getRoomData,
} from "~/share/share-embedded-data"
import { useRoomSharePhoneSubmission } from "~/hooks/use-phone-submission"
import { useRoomShareEmailSubmission } from "~/hooks/use-email-submission"
import { useCollectEmail } from "~/hooks/use-collect-email"
import { useCallback } from "react"
import { EVENT_IDS, SCREENS, SHARE_PAGES } from "~/common/analytics"
import { useScreenTracking } from "~/hooks/use-screen-tracking"

export const ShareMessageScreen = (): JSX.Element => {
  const messageData = getMessageData()
  const episodeData = getEpisodeData()
  const roomData = getRoomData()
  const collectEmail = useCollectEmail()

  // The shared item this screen is unfurling (server-rendered). item_type grows
  // as more shareable content types are added (live spaces, games, ...).
  const sharedItem = messageData
    ? { item_type: "message", item_id: messageData.id }
    : episodeData
      ? { item_type: "episode", item_id: episodeData.id }
      : undefined

  const appEnvironment = getMetaContent<string>("APP_ENVIRONMENT")
  const deeplinkUrl = getRoomDeeplinkUrl(appEnvironment, roomData.id)
  const phoneSubmission = useRoomSharePhoneSubmission()
  const emailSubmission = useRoomShareEmailSubmission()

  const roomId = roomData.id
  const roomHandle = roomData.roomHandle ?? undefined

  useScreenTracking(SCREENS.Share, {
    share_page: SHARE_PAGES.Message,
    room_id: roomId,
    room_handle: roomHandle,
    deeplink_url: deeplinkUrl,
    ...sharedItem,
  })

  const onJoinPress = useCallback(async () => {
    const successMessage = skipWaitlistSuccessMessage(collectEmail)
    const successTitle = SKIP_WAITLIST_TITLE
    const inviterData = getInviterData()

    const trackingData = {
      unique_id: EVENT_IDS.SHARE_SCREEN_SKIP_WAITLIST,
      share_page: SHARE_PAGES.Message,
      inviter_id: inviterData?.id,
      inviter_username: inviterData?.username,
      room_id: roomId,
      deeplink_url: deeplinkUrl,
      room_handle: roomHandle,
    }

    if (collectEmail) {
      await emailSubmission.submitEmail({ successMessage, successTitle, roomId, trackingData })
    } else {
      await phoneSubmission.submitPhoneNumber({
        successMessage,
        successTitle,
        roomId,
        trackingData,
      })
    }
  }, [collectEmail, emailSubmission, phoneSubmission, roomId, deeplinkUrl, roomHandle])

  const inputProps = collectEmail
    ? {
        inputMode: "email" as const,
        emailValue: emailSubmission.emailValue,
        setEmailValue: emailSubmission.setEmailValue,
      }
    : {
        inputMode: "phone" as const,
        phoneValue: phoneSubmission.phoneValue,
        setPhoneValue: phoneSubmission.setPhoneValue,
      }

  return (
    <ShareScreenBase
      analyticsProps={{
        room_id: roomId,
        room_handle: roomHandle,
      }}
      onJoinPress={onJoinPress}
      deeplinkUrl={deeplinkUrl}
      {...inputProps}
      renderContent={() =>
        roomData && <RoomContentCard roomData={roomData} className="max-w-[640px]" />
      }
    />
  )
}

export default ShareMessageScreen
